import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { compare, inr, inrShort } from "@/lib/api";
import { GitCompareArrows, Loader2 } from "lucide-react";

const QUALITY_COLORS = { standard: "#64748B", premium: "#F97316", luxury: "#F59E0B" };

export const ComparisonView = ({ builtupArea, floors }) => {
  const [data, setData] = useState(null);
  const [quality, setQuality] = useState("premium");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!builtupArea) return;
    let active = true;
    setLoading(true);
    compare({ builtup_area: builtupArea, floors })
      .then((d) => active && setData(d))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [builtupArea, floors]);

  const chartData = data
    ? data.matrix.map((r) => ({ city: r.city, cost: r.costs[quality], tier: r.tier }))
    : [];

  return (
    <Card className="bg-slate-900/70 border-slate-800 rounded-2xl p-6 sm:p-8" data-testid="comparison-view">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-500/15 grid place-items-center">
            <GitCompareArrows className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold text-white">City & Quality Comparison</h3>
            <p className="text-sm text-slate-400">
              Total cost across Indian cities {data ? `· ${inr(data.total_area)} sq.ft` : ""}
            </p>
          </div>
        </div>
        <div className="flex gap-1.5 bg-slate-950 rounded-xl p-1 border border-slate-800">
          {["standard", "premium", "luxury"].map((q) => (
            <button
              key={q}
              onClick={() => setQuality(q)}
              data-testid={`compare-quality-${q}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
                quality === q ? "bg-orange-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="h-72 grid place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
        </div>
      ) : (
        <>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis
                  dataKey="city"
                  angle={-40}
                  textAnchor="end"
                  height={60}
                  tick={{ fill: "#94a3b8", fontSize: 11 }}
                  stroke="#334155"
                />
                <YAxis
                  tickFormatter={(v) => inrShort(v)}
                  tick={{ fill: "#94a3b8", fontSize: 11 }}
                  stroke="#334155"
                />
                <Tooltip
                  formatter={(v) => [`₹${inr(v)}`, "Total Cost"]}
                  cursor={{ fill: "rgba(249,115,22,0.08)" }}
                  contentStyle={{
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                />
                <Bar dataKey="cost" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={QUALITY_COLORS[quality]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {data && (
            <div className="mt-6 overflow-x-auto rounded-xl border border-slate-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400">
                    <th className="text-left px-4 py-3 font-medium">City</th>
                    {data.quality_tiers.map((q) => (
                      <th key={q.id} className="text-right px-4 py-3 font-medium">{q.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.matrix.map((r) => (
                    <tr key={r.city} className="border-b border-slate-800/60" data-testid={`compare-row-${r.city}`}>
                      <td className="px-4 py-2.5 text-white font-medium">
                        {r.city} <span className="text-slate-600 text-xs">· {r.tier}</span>
                      </td>
                      {data.quality_tiers.map((q) => (
                        <td key={q.id} className="text-right px-4 py-2.5 font-mono-data text-slate-300">
                          ₹{inr(r.costs[q.id])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
};
